import type { PropsWithChildren } from "react";
import React, { forwardRef, Fragment, memo, useCallback } from "react";
import clsx from "clsx";
import styles from "./Grades.module.scss";
import type { CarConfigDispatchType } from "../../../../../../../redux/store";
import { useCarConfigSelector } from "../../../../../../../redux/store";
import Text from "../../../../../../../../shared-components/css-modules/lexus/atoms/Text";
import Button from "../../../../../../../../shared-components/css-modules/lexus/atoms/Button";
import type { AtmosphereType } from "../../../../../../../utils/types/AtmosphereType";
import Price from "../../../../../common/price/Price";
import { getVisualForGradeImage } from "../../../../../../../../../../common-deprecated/settings/utils/ccisSettingUtils";
import { CarConfigStep } from "../../../../../../../../../../common-deprecated/features/apheleia/utils/carConfigNavigationUtils";
import { changeCarId } from "../../../../../../../utils/navigationUtils";
import { setCarGrade } from "../../../../../../../redux/slices/CarConfigCarSlice";
import { trackSelectAtmosphereGrade, trackViewPackOptions } from "../../../../../../../utils/tracking";
import { removeAllPacksAndExtras } from "../../../../../../../redux/slices/CarConfigBasketSlice";
import { CarConfigLabel } from "../../../../../../../utils/labelUtils";
import { setNavigationIds } from "../../../../../../../redux/slices/CarConfigNavigationSlice";
import { useDispatch } from "../../../../../../../../../../shared-logic/redux/store";

const Grade = forwardRef<
    HTMLElement,
    PropsWithChildren<{ grade: AtmosphereType; onCompareGrade?: (gradeId: string, gradeName: string) => void }>
>(({ grade, onCompareGrade }, ref) => {
    const dispatch = useDispatch<CarConfigDispatchType>();
    const car = useCarConfigSelector((state) => state.car);
    const currentGrade = car.grade;
    const isActive = currentGrade.id === grade.id;
    const commonSettings = useCarConfigSelector((state) => state.commonSettings);

    let wheelDrive = null;
    const hasFrontWeelDrive = grade.wheeldrives.find(({ code }) => code === "2WD");
    const hasAllWeelDrive = grade.wheeldrives.find(({ code }) => code === "4WD");
    if (hasFrontWeelDrive && hasAllWeelDrive) {
        wheelDrive = <CarConfigLabel label="carConfigLbxGradeWheeldriveFrontOrAll" />;
    } else if (hasFrontWeelDrive) {
        wheelDrive = <CarConfigLabel label="carConfigLbxGradeWheeldriveFront" />;
    } else if (hasAllWeelDrive) {
        wheelDrive = <CarConfigLabel label="carConfigLbxGradeWheeldriveAll" />;
    }

    const onClickHandler = useCallback(() => {
        trackSelectAtmosphereGrade();

        // Update the URL with the new CarID due to the selected motorization
        // Also update this in redux as this does not happen automatically
        dispatch(setNavigationIds({ modelId: car.model.id, carId: grade.wheeldrives[0].defaultCarId }));
        dispatch(removeAllPacksAndExtras());
        changeCarId(CarConfigStep.Configure, car.model.id, grade.wheeldrives[0].defaultCarId);

        dispatch(
            setCarGrade({
                id: grade.id,
                name: grade.name,
                webtitleSectionName: grade.webtitleSections.grade,
            }),
        );
    }, [car]);

    const onCompareClick = useCallback(
        (event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement, MouseEvent>) => {
            event.preventDefault();
            event.stopPropagation();
            trackViewPackOptions(grade.name);
            onCompareGrade?.(grade.id, grade.name);
        },
        [onCompareGrade, grade],
    );

    return (
        <>
            <article className={clsx(styles.grade, isActive && styles.current)} ref={ref}>
                <header className={styles["grade-header"]}>
                    <Text tag="h3" variant="apheleia-h4" className={styles["grade-title"]}>
                        {grade.webtitleSections.grade || grade.webtitleSections.model}
                    </Text>

                    <Text tag="div" variant="apheleia-body" className={styles.price}>
                        <Price price={grade.price} />
                    </Text>
                </header>
                <img
                    {...getVisualForGradeImage(
                        commonSettings,
                        car.model.id,
                        grade.id,
                        undefined,
                        {
                            type: "png",
                            width: 364 * 2, // For hDPI displays
                            height: 160 * 2, // For hDPI displays
                            bgColour: "",
                        },
                        3,
                    )}
                    alt={`${grade.webtitleSections.model} ${grade.webtitleSections.grade}`}
                />
                <div className={styles["grade-info"]}>
                    <dl>
                        <dt>
                            <CarConfigLabel label="carConfigLbxGradeWheeldrive" />
                        </dt>
                        <dd>{wheelDrive}</dd>
                        <dt>
                            <CarConfigLabel label="carConfigLbxGradeExteriorColor" />
                        </dt>
                        <dd>
                            {grade.exteriorColors.roof === null ? (
                                <CarConfigLabel label="carConfigLbxGradeExteriorColorMonotone" />
                            ) : (
                                <CarConfigLabel label="carConfigLbxGradeExteriorColorBiTone" />
                            )}
                        </dd>
                        <dt>
                            <CarConfigLabel label="carConfigLbxGradeInteriorColor" />
                        </dt>
                        <dd>{grade.upholstery.name}</dd>
                        <dt>
                            <CarConfigLabel label="carConfigLbxGradeWheels" />
                        </dt>
                        <dd>{grade.wheel.name}</dd>
                        {grade.wheeldrives[0].packs.length !== 0 && (
                            <>
                                <dt>
                                    <CarConfigLabel label="carConfigLbxGradeAvailablePacks" />
                                </dt>
                                <dd>
                                    {grade.wheeldrives[0].packs
                                        .filter((pack) => typeof pack.standard !== "undefined")
                                        .map((pack, index) => (
                                            <Fragment key={pack.id}>
                                                {pack.name}
                                                {index === grade.wheeldrives[0].packs.length - 2
                                                    ? " & "
                                                    : grade.wheeldrives[0].packs.length !== 1 &&
                                                        index !== grade.wheeldrives[0].packs.length - 1
                                                      ? ", "
                                                      : null}
                                            </Fragment>
                                        ))}
                                </dd>
                            </>
                        )}
                    </dl>
                    <Button variant="btn-text-upper" onClick={onCompareClick}>
                        <CarConfigLabel label="carConfigLbxGradeViewPacks" />
                    </Button>
                </div>
                <footer className={styles["grade-footer"]}>
                    <Button
                        variant="btn-lbx"
                        disabled={isActive}
                        sizing="full-width"
                        className={styles["grade-button"]}
                        onClick={onClickHandler}
                    >
                        <CarConfigLabel label={isActive ? "carConfigLbxSelected" : "carConfigLbxSelect"} />
                    </Button>
                </footer>
            </article>
        </>
    );
});

export default memo(Grade);
